import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {
  selectSchedulerLoading,
  selectVerifyFolderResult,
  verifyFolder,
} from '../redux/SchedulerRedux'
import {useDispatch} from 'react-redux'
import useDrivePicker from 'react-google-drive-picker'
import {toast} from 'react-toastify'
import {PickerCallback} from 'react-google-drive-picker/dist/typeDefs'

interface Props {
  show: boolean
  onHide: () => void
  onCompleted: (verificationResult: any) => void
}

// Define step status types
type StepStatus = 'not-started' | 'in-progress' | 'completed' | 'failed'

// Define step interface
interface Step {
  id: number
  title: string
  status: StepStatus
}

const FolderVerifyStepsModal: React.FC<Props> = ({show, onHide, onCompleted}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const verificationResult = useAppSelector(selectVerifyFolderResult)
  const isChecking = useAppSelector(selectSchedulerLoading)

  // Setup Google Drive Picker
  const [openPicker] = useDrivePicker()
  const [selectedFolder, setSelectedFolder] = useState<{id: string; name: string} | null>(null)

  // State to track if picker is open and if we should reopen modal
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  // Step management state
  const [currentStep, setCurrentStep] = useState(1)
  const [understood, setUnderstood] = useState(false)
  const [folderStructureCreated, setFolderStructureCreated] = useState(false)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)

  // Description data for folder structure elements
  const folderDescriptions = [
    {key: 'shared_folder', value: 'Scheduler.FOLDER.STRUCTURE.SHARED_DESC'},
    {key: 'execute_folder', value: 'Scheduler.FOLDER.STRUCTURE.EXECUTE_DESC'},
    {key: 'order_folder', value: 'Scheduler.FOLDER.STRUCTURE.ORDER_DESC'},
    {key: 'image_file', value: 'Scheduler.FOLDER.STRUCTURE.IMAGE_DESC'},
    {key: 'submit_folder', value: 'Scheduler.FOLDER.STRUCTURE.SUBMIT_DESC'},
    {key: 'output_file', value: 'Scheduler.FOLDER.STRUCTURE.OUTPUT_DESC'},
  ]
  const [steps, setSteps] = useState<Step[]>([
    {
      id: 1,
      title: intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.PREPARE'}),
      status: 'not-started',
    },
    {
      id: 2,
      title: intl.formatMessage({id: 'Scheduler.FOLDER.SELECT'}),
      status: 'not-started',
    },
    {
      id: 3,
      title: intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.TITLE'}),
      status: 'not-started',
    },
    {
      id: 4,
      title: intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.VERIFICATION_RESULT'}),
      status: 'not-started',
    },
  ])

  // Function to get a random badge color class
  const getRandomBadgeColor = () => {
    const colors = ['primary', 'success', 'info', 'warning', 'danger']
    const randomIndex = Math.floor(Math.random() * colors.length)
    return `badge-light-${colors[randomIndex]}`
  }

  // Handle folder selection
  const handleOpenPicker = () => {
    // Close the modal and set flag to reopen it later
    setIsPickerOpen(true)

    // Small delay to ensure modal is fully closed
    setTimeout(() => {
      openPicker({
        clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID as string,
        developerKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
        viewId: 'FOLDERS',
        setSelectFolderEnabled: true,
        showUploadView: false,
        showUploadFolders: true,
        supportDrives: true,
        multiselect: false,
        customScopes: [
          'https://www.googleapis.com/auth/drive.file',
          'https://www.googleapis.com/auth/drive.readonly',
        ],
        callbackFunction: (data: PickerCallback) => {
          console.log('Picker data:', data)
          if (data.action === 'picked') {
            const folder = data.docs[0]
            setIsPickerOpen(false)
            // Check if selected item is a folder
            if (folder.mimeType === 'application/vnd.google-apps.folder') {
              setSelectedFolder({
                id: folder.id,
                name: folder.name,
              })
              const newSteps = [...steps]
              newSteps[1].status = 'completed' // Step 2 (index 1) is now folder selection
              setSteps(newSteps)
            } else {
              toast.error(intl.formatMessage({id: 'Scheduler.FOLDER.MUST_BE_FOLDER'}))
            }
          }
        },
      })
    }, 300)
  }

  // Reset steps when modal opens/closes
  useEffect(() => {
    if (show) {
      setCurrentStep(1)
      setUnderstood(false)
      setFolderStructureCreated(false)
      setHoveredKey(null)
      setSelectedFolder(null)
      setSteps((steps) => steps.map((step) => ({...step, status: 'not-started'})))
    }
  }, [show])

  // Update step status when verification completes
  useEffect(() => {
    if (verificationResult) {
      const newSteps = [...steps]
      newSteps[currentStep - 1].status = verificationResult.isSuccess ? 'completed' : 'failed'
      setSteps(newSteps)

      // If verification is successful and we're on step 2 (folder selection),
      // we need to update step 0 (first step) to completed since folder is selected
      if (verificationResult.isSuccess && currentStep === 2 && selectedFolder) {
        newSteps[0].status = 'completed'
        setSteps(newSteps)
      }
    }
  }, [verificationResult, currentStep, selectedFolder, steps])

  // Handle verify action for the current step
  const handleVerifyStep = () => {
    const newSteps = [...steps]
    newSteps[currentStep - 1].status = 'in-progress'
    setSteps(newSteps)
    if (selectedFolder) {
      dispatch(verifyFolder(selectedFolder.id))
    }
  }

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < steps.length) {
      // For step 2 (now folder selection), check if a folder is selected
      if (currentStep === 2 && !selectedFolder) {
        return
      }

      // If this is verification step, perform verification before proceeding
      if (currentStep === 4) {
        handleVerifyStep()
        // Only proceed if verification is successful
        if (verificationResult?.isSuccess) {
          setCurrentStep(currentStep + 1)
        }
      } else {
        // For other steps, just mark as completed and proceed
        const newSteps = [...steps]
        newSteps[currentStep - 1].status = 'completed'
        setSteps(newSteps)
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Determine if Next button should be enabled
  const isNextEnabled = () => {
    if (currentStep === 1) return folderStructureCreated // Step 1 requires folder structure confirmation
    if (currentStep === 2) return !!selectedFolder // Step 2 is now folder selection
    if (currentStep === 4) return understood
    return true
  }

  // Render the step indicator component
  const renderStepIndicator = () => {
    return (
      <div className='stepper stepper-pills '>
        <div className='stepper-nav flex-center flex-wrap mb-10'>
          {steps.map((step) => (
            <div
              className={'stepper-item mx-8 my-4 ' + (currentStep === step.id ? 'current' : '')}
              data-kt-stepper-element='nav'
            >
              <div className='stepper-wrapper d-flex align-items-center'>
                <div className='stepper-icon w-40px h-40px'>
                  <i className='stepper-check fas fa-check'></i>

                  <span className='stepper-number '>{step.id}</span>
                </div>

                <div className='stepper-label'>
                  <h3 className='stepper-title'>
                    {intl.formatMessage({id: 'SYSTEM.STEP'})} {step.id}
                  </h3>

                  <div className='stepper-desc'>{step.title}</div>
                </div>
              </div>

              <div className='stepper-line h-40px'></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Render step 1 content (Prepare folder structure)
  const renderStep1 = () => {
    const folderNameToUse = selectedFolder?.name || 'YourFolder'

    // Helper function to create hoverable folder elements
    const createHoverableElement = (content: string, key: string, className = '') => (
      <span
        className={`hoverable-element ${className} ${hoveredKey === key ? 'highlighted' : ''}`}
        onMouseEnter={() => setHoveredKey(key)}
        onMouseLeave={() => setHoveredKey(null)}
        style={{
          cursor: 'pointer',
          padding: '2px 4px',
          borderRadius: '3px',
          transition: 'all 0.2s ease',
          backgroundColor: hoveredKey === key ? 'rgba(255, 193, 7, 0.2)' : 'transparent',
        }}
      >
        {content}
      </span>
    )

    return (
      <div className='step-content'>
        <h2 className='fw-bold mb-4'>
          {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.PREPARE'})}
        </h2>

        <div className='row gx-4'>
          {' '}
          {/* Increased gap between columns */}
          {/* Left column - Before Processing */}
          <div className='col-md-5'>
            <div className='card bg-light-primary border-primary border border-dashed h-100'>
              <div className='card-header border-0 bg-light-primary'>
                <h3 className='card-title text-primary fw-bold'>
                  <i className='fas fa-folder me-2'></i>
                  {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.BEFORE'})}
                </h3>
              </div>
              <div className='card-body'>
                <pre className='m-0 text-dark fs-6' style={{lineHeight: '1.8'}}>
                  📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.SHARED'}),
                    'shared_folder',
                    'text-success fw-bold'
                  )}
                  /{'\n'}└──📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.EXECUTE'}),
                    'execute_folder',
                    'text-info fw-bold'
                  )}
                  (YYMMDD)/
                  {'\n'} └── 📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.ORDER'}),
                    'order_folder',
                    'text-warning fw-bold'
                  )}
                  /{'\n'} | └── 🖼️{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.IMAGE'}),
                    'image_file',
                    'text-secondary'
                  )}
                  {'\n'} ├── 🖼️{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.IMAGE'}),
                    'image_file',
                    'text-secondary'
                  )}
                </pre>
              </div>
            </div>
          </div>
          {/* Visual separator */}
          <div className='col-md-1 d-flex align-items-center justify-content-center'>
            <div
              className='vr'
              style={{height: '300px', width: '3px', backgroundColor: '#e9ecef'}}
            ></div>
            <div className='position-absolute bg-white px-3'>
              <i className='fas fa-arrow-right text-primary fs-2'></i>
            </div>
          </div>
          {/* Right column - After Processing */}
          <div className='col-md-6'>
            <div className='card bg-light-success border-success border border-dashed h-100'>
              <div className='card-header border-0 bg-light-success'>
                <h3 className='card-title text-success fw-bold'>
                  <i className='fas fa-folder-open me-2'></i>
                  {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.AFTER'})}
                </h3>
              </div>
              <div className='card-body'>
                <pre className='m-0 text-dark fs-6' style={{lineHeight: '1.8'}}>
                  📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.SHARED'}),
                    'shared_folder',
                    'text-success fw-bold'
                  )}
                  /{'\n'}└──📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.EXECUTE'}),
                    'execute_folder',
                    'text-info fw-bold'
                  )}
                  (YYMMDD)/
                  {'\n'} └── 📁{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.ORDER'}),
                    'order_folder',
                    'text-warning fw-bold'
                  )}
                  /{'\n'} | └── 🖼️{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.IMAGE'}),
                    'image_file',
                    'text-secondary'
                  )}
                  {'\n'} |{' '}
                  {createHoverableElement(
                    `└── 📁 ${intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.SUBMIT'})}*`,
                    'submit_folder',
                    'text-success fw-bold'
                  )}
                  {'\n'} |{' '}
                  {createHoverableElement(
                    `└── 🖼️ ${intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.OUTPUT'})}*`,
                    'output_file',
                    'text-danger fw-bold'
                  )}
                  {'\n'} ├── 🖼️{' '}
                  {createHoverableElement(
                    intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.IMAGE'}),
                    'image_file',
                    'text-secondary'
                  )}
                  {'\n'}{' '}
                  {createHoverableElement(
                    `└── 📁 ${intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.SUBMIT'})}*`,
                    'submit_folder',
                    'text-success fw-bold'
                  )}
                  {'\n'}{' '}
                  {createHoverableElement(
                    `└── 🖼️ ${intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.OUTPUT'})}*`,
                    'output_file',
                    'text-danger fw-bold'
                  )}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Description Boxes */}
        <div className='mt-6'>
          <h4 className='fw-bold mb-4'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.ELEMENTS_GUIDE'})}
          </h4>
          <div className='row g-3'>
            {folderDescriptions.map((desc, index) => (
              <div key={desc.key} className='col-md-6 col-lg-6'>
                <div
                  className={`card border-2 transition-all duration-200 ${
                    hoveredKey === desc.key
                      ? 'border-warning bg-light-warning shadow-lg'
                      : 'border-light bg-light'
                  }`}
                  style={{
                    transition: 'all 0.3s ease',
                    transform: hoveredKey === desc.key ? 'translateY(-2px)' : 'none',
                    cursor: hoveredKey === desc.key ? 'default' : 'pointer',
                  }}
                  onMouseEnter={() => setHoveredKey(desc.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                >
                  <div className='card-body p-4'>
                    <div className='d-flex align-items-center mb-2'>
                      <div
                        className={`symbol symbol-30px me-3 ${
                          hoveredKey === desc.key ? 'symbol-warning' : 'symbol-light-primary'
                        }`}
                      >
                        <span className='symbol-label'>
                          <i
                            className={`fas ${
                              desc.key.includes('folder') ? 'fa-folder' : 'fa-file-image'
                            } fs-6`}
                          ></i>
                        </span>
                      </div>
                      <h6 className='fw-bold mb-0'>
                        {intl.formatMessage({
                          id: `Scheduler.FOLDER.STRUCTURE.${desc.key.toUpperCase()}`,
                        })}
                      </h6>
                    </div>
                    <p className='text-muted fs-7 mb-0'>{intl.formatMessage({id: desc.value})}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Original notes section with improved styling */}
        <div className='mt-6 bg-white rounded p-4 border border-dashed border-gray-300'>
          <h5 className='fw-bold mb-3 text-primary'>
            <i className='fas fa-info-circle me-2'></i>
            {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.IMPORTANT_NOTES'})}
          </h5>
          <div className='row'>
            <div className='d-flex align-items-start col-md-6'>
              <span className='bullet bullet-dot bg-primary me-3 mt-1'></span>
              <span className='text-dark fs-7'>
                {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.NOTE1'})}
              </span>
            </div>
            <div className='d-flex align-items-start col-md-6'>
              <span className='bullet bullet-dot bg-primary me-3 mt-1'></span>
              <span className='text-dark fs-7'>
                {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.NOTE2'})}
              </span>
            </div>
          </div>
        </div>

        {/* Add confirmation checkbox */}
        <div className='form-check form-check-custom form-check-solid mb-5 mt-6 bg-light-primary rounded p-4'>
          <input
            className='form-check-input'
            type='checkbox'
            id='folderStructureCreated'
            checked={folderStructureCreated}
            onChange={(e) => setFolderStructureCreated(e.target.checked)}
          />
          <label
            className='form-check-label fw-semibold text-primary'
            htmlFor='folderStructureCreated'
          >
            {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.CONFIRM_CREATED'})}
          </label>
        </div>
      </div>
    )
  }

  // Render step 2 content (Select folder)
  const renderStep2 = () => {
    return (
      <div className='step-content'>
        <h2 className='fw-bold mb-4'>{intl.formatMessage({id: 'Scheduler.FOLDER.SELECT'})}</h2>
        <div className='d-flex align-items-center mt-3 mb-5'>
          <div className='symbol symbol-information me-4'>
            <i className='fas fa-info-circle fs-2 text-primary'></i>
          </div>
          <div className='fs-6 text-gray-600'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.SELECT.DESCRIPTION'})}
          </div>
        </div>

        {selectedFolder ? (
          <div className='card bg-light-success border-success border border-dashed mb-7'>
            <div className='card-body d-flex align-items-center'>
              <i className='fas fa-folder-open text-success fs-2 me-4'></i>
              <div>
                <h3 className='fs-4 fw-bold mb-1'>{selectedFolder.name}</h3>
                <span className='text-muted'>{selectedFolder.id}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className='card bg-light-primary border-primary border border-dashed mb-7'>
            <div className='card-body text-center py-10'>
              <i className='fas fa-folder-plus text-primary fs-3x mb-5'></i>
              <h3 className='fs-2 fw-bolder text-dark mb-2'>
                {intl.formatMessage({id: 'Scheduler.FOLDER.SELECT.PROMPT'})}
              </h3>
              <button className='btn btn-primary px-6 mt-3' onClick={handleOpenPicker}>
                <i className='fas fa-folder-open me-2'></i>
                {intl.formatMessage({id: 'Scheduler.FOLDER.SELECT.BUTTON'})}
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render step 3 content (Share folder)
  const renderStep3 = () => {
    return (
      <div className='step-content'>
        <h2 className='fw-bold mb-4'>{intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.TITLE'})}</h2>
        <div className='d-flex align-items-center mt-3 mb-5'>
          <div className='symbol symbol-information me-4'>
            <i className='fas fa-info-circle fs-2 text-primary'></i>
          </div>
          <div className='fs-6 text-gray-600'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.DESCRIPTION'})}
          </div>
        </div>
        <div className='d-flex align-items-center border border-dashed border-gray-300 rounded p-5 mb-7'>
          <i className='fas fa-envelope fs-3 text-primary me-4'></i>
          <span className='fw-semibold fs-6 me-2 flex-grow-1'>
            {process.env.REACT_APP_GOOGLE_OAUTH2_ACCOUNT}
          </span>
          <button
            className='btn btn-sm btn-light-primary'
            onClick={() =>
              navigator.clipboard.writeText(process.env.REACT_APP_GOOGLE_OAUTH2_ACCOUNT || '')
            }
          >
            <i className='fas fa-copy'></i> {intl.formatMessage({id: 'SYSTEM.COPY'})}
          </button>
        </div>
        <div className='alert alert-primary d-flex align-items-center p-5'>
          <i className='fas fa-question-circle fs-2 me-4 text-primary'></i>
          <div className='d-flex flex-column'>
            <span>{intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.HELP'})}</span>
            <a
              href='https://support.google.com/drive/answer/7166529'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary fw-bold mt-2'
            >
              {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.HELP_LINK'})}
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Render step 4 content (Verification results)
  const renderStep4 = () => {
    if (isChecking) {
      return (
        <div className='d-flex flex-column align-items-center py-10'>
          <div className='position-relative mb-7'>
            <span className='spinner-border spinner-border-xl text-primary'></span>
            <span className='position-absolute top-50 start-50 translate-middle'>
              <i className='fas fa-folder-open text-primary fs-3'></i>
            </span>
          </div>
          <h3 className='fs-2 fw-bolder text-dark mb-2'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.VERIFYING'})}
          </h3>
          <div className='text-gray-500 fs-6'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.WAIT'})}
          </div>
        </div>
      )
    }

    if (verificationResult) {
      return (
        <div className='step-content'>
          <h2 className='fw-bold mb-4'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.VERIFICATION_RESULT'})}
          </h2>
          <div
            className={`card shadow-none bg-light-${
              verificationResult.isSuccess ? 'success' : 'danger'
            } rounded border-${
              verificationResult.isSuccess ? 'success' : 'danger'
            } border border-dashed mb-7`}
          >
            <div className='card-body p-2'>
              <div
                className={`alert bg-light-${
                  verificationResult.isSuccess ? 'success' : 'danger'
                } d-flex align-items-center p-5`}
              >
                <span className='svg-icon svg-icon-2hx me-4'>
                  <i
                    className={`fas ${
                      verificationResult.isSuccess ? 'fa-check-circle' : 'fa-exclamation-circle'
                    } fs-2`}
                  ></i>
                </span>
                <span className='fs-6 fw-bold'>
                  {verificationResult.isSuccess
                    ? intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.SUCCESS'})
                    : verificationResult.message}
                </span>
              </div>

              {verificationResult.isSuccess && (
                <>
                  <div className='row g-3 mb-7'>
                    {/* Existing stat cards */}
                    <div className='col-6 col-lg-3'>
                      <div className='card shadow-sm'>
                        <div className='card-body p-5'>
                          <div className='d-flex flex-column'>
                            <div className='fs-7 fw-bold text-gray-400'>
                              {intl.formatMessage({id: 'Scheduler.FOLDER.EXECUTE_DATES'})}
                            </div>
                            <div className='d-flex align-items-center'>
                              <div
                                className={`bg-light-${
                                  verificationResult.canWrite ? 'success' : 'danger'
                                } rounded w-35px h-35px d-flex align-items-center justify-content-center me-3`}
                              >
                                <i className='fas fa-calendar-alt text-dark fs-4'></i>
                              </div>
                              <span className='fs-2 fw-bolder text-dark'>
                                {verificationResult.totalDates}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-lg-3'>
                      <div className='card shadow-sm'>
                        <div className='card-body p-5'>
                          <div className='d-flex flex-column'>
                            <div className='fs-7 fw-bold text-gray-400'>
                              {intl.formatMessage({id: 'Scheduler.FOLDER.ORDER_NAMES'})}
                            </div>
                            <div className='d-flex align-items-center'>
                              <div
                                className={`bg-light-${
                                  verificationResult.canWrite ? 'success' : 'danger'
                                } rounded w-35px h-35px d-flex align-items-center justify-content-center me-3`}
                              >
                                <i className='fas fa-folder-open text-dark fs-4'></i>
                              </div>
                              <span className='fs-2 fw-bolder text-dark'>
                                {verificationResult.totalOrders}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-lg-3'>
                      <div className='card shadow-sm'>
                        <div className='card-body p-5'>
                          <div className='d-flex flex-column'>
                            <div className='fs-7 fw-bold text-gray-400'>
                              {intl.formatMessage({id: 'Scheduler.FOLDER.IMAGES'})}
                            </div>
                            <div className='d-flex align-items-center'>
                              <div
                                className={`bg-light-${
                                  verificationResult.canWrite ? 'success' : 'danger'
                                } rounded w-35px h-35px d-flex align-items-center justify-content-center me-3`}
                              >
                                <i className='fas fa-image text-dark fs-4'></i>
                              </div>
                              <span className='fs-2 fw-bolder text-dark'>
                                {verificationResult.totalFiles}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-lg-3'>
                      <div className='card shadow-sm'>
                        <div className='card-body p-5'>
                          <div className='d-flex flex-column'>
                            <div className='fs-7 fw-bold text-gray-400'>
                              {intl.formatMessage({id: 'Scheduler.FOLDER.WRITE_PERMISSION'})}
                            </div>
                            <div className='d-flex align-items-center'>
                              <div
                                className={`bg-light-${
                                  verificationResult.canWrite ? 'success' : 'danger'
                                } rounded w-35px h-35px d-flex align-items-center justify-content-center me-3`}
                              >
                                <i className='fas fa-pen text-dark fs-4'></i>
                              </div>
                              <span className='fs-2 fw-bolder text-dark'>
                                {verificationResult.canWrite
                                  ? intl.formatMessage({id: 'YES'})
                                  : intl.formatMessage({id: 'NO'})}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Files Table */}
                  {verificationResult.orders && verificationResult.orders.length > 0 && (
                    <div className='card shadow-sm'>
                      <div className='card-header'>
                        <h3 className='card-title fw-bold'>
                          {intl.formatMessage({id: 'Scheduler.FOLDER.ORDER_FILES'})}
                        </h3>
                      </div>
                      <div className='card-body p-0'>
                        <div className='table-responsive'>
                          <table className='table table-hover table-rounded table-striped border gy-5 gs-7'>
                            <thead>
                              <tr className='fw-bold fs-6 text-gray-800 border-bottom border-gray-200'>
                                <th>{intl.formatMessage({id: 'Scheduler.FOLDER.ORDER_DATE'})}</th>
                                <th>{intl.formatMessage({id: 'Scheduler.FOLDER.ORDER_NAME'})}</th>
                                <th>{intl.formatMessage({id: 'Scheduler.FOLDER.FILES_COUNT'})}</th>
                                <th>{intl.formatMessage({id: 'Scheduler.FOLDER.TAGS'})}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {verificationResult.orders.map((file, index) => (
                                <tr key={index}>
                                  <td className='fw-semibold'>{file.executionDate}</td>
                                  <td>{file.orderName}</td>
                                  <td>{file.numberOfFiles}</td>
                                  <td>
                                    {file.tags &&
                                      file.tags.map((tag, tagIndex) => {
                                        // Generate a random badge color class for each tag
                                        const badgeColorClass = getRandomBadgeColor()
                                        return (
                                          <span
                                            key={tagIndex}
                                            className={`badge ${badgeColorClass} me-2 mb-1`}
                                          >
                                            {tag}
                                          </span>
                                        )
                                      })}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Add understanding checkbox for successful verification */}
          {verificationResult.isSuccess && (
            <div className='form-check form-check-custom form-check-solid mb-5'>
              <input
                className='form-check-input'
                type='checkbox'
                id='understood'
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
              />
              <label className='form-check-label fw-semibold' htmlFor='understood'>
                {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.UNDERSTAND'})}
              </label>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className='text-center py-10'>
        <i className='fas fa-folder-open text-primary fs-3x mb-5'></i>
        <h3 className='fs-2 fw-bolder text-dark mb-2'>
          {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.NO_VERIFICATION'})}
        </h3>

        <div className='text-gray-500 fs-6'>
          {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.CLICK_VERIFY'})}
        </div>
      </div>
    )
  }

  // Render the active step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      default:
        return null
    }
  }

  return (
    <Modal show={show && !isPickerOpen} onHide={onHide} centered size='xl' className='wizard-modal'>
      <Modal.Header className='border-0 justify-content-end'>
        <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={onHide}>
          <span className='svg-icon svg-icon-1'>
            <i className='fas fa-times'></i>
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className='mx-5 pt-0'>
        <div className='mb-8'>
          <h1 className='fw-bold mb-4 text-center'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.TITLE'})}
          </h1>
          <div className='text-muted fw-semibold fs-5 text-center mb-5'>
            {intl.formatMessage({id: 'Scheduler.FOLDER.STRUCTURE.SUB_TITLE'})}
          </div>

          {/* YouTube Video Guide */}
          <div className='d-flex justify-content-center mb-7'>
            <div className='card bg-light-info border-info border border-dashed rounded-3 shadow-sm'>
              <div className='card-body p-4 text-center d-flex'>
                <div className='d-flex align-items-center justify-content-center mb-3'>
                  <div className='symbol symbol-50px bg-info me-3'>
                    <span className='symbol-label'>
                      <i className='fab fa-youtube text-danger fs-1'></i>
                    </span>
                  </div>
                  <div className='text-start'>
                    <h5 className='fw-bold text-dark mb-1'>
                      {intl.formatMessage({id: 'Scheduler.FOLDER.VIDEO_GUIDE.TITLE'})}
                    </h5>
                    <span className='text-muted fs-7'>
                      {intl.formatMessage({id: 'Scheduler.FOLDER.VIDEO_GUIDE.SUBTITLE'})}
                    </span>
                  </div>
                </div>
                <button
                  className='btn btn-info btn-sm px-4 ms-4'
                  onClick={() => window.open(process.env.REACT_APP_STORAGE_YOUTUBE_LINK, '_blank')}
                >
                  <i className='fas fa-play me-2'></i>
                  {intl.formatMessage({id: 'Scheduler.FOLDER.VIDEO_GUIDE.WATCH_NOW'})}
                </button>
              </div>
            </div>
          </div>

          {/* Step indicator at the top */}
          {renderStepIndicator()}
        </div>

        {/* Step content area */}
        <div className='step-container py-5'>{renderStepContent()}</div>
      </Modal.Body>
      <Modal.Footer className='border-0 justify-content-between py-5'>
        <button
          className='btn btn-light px-6'
          onClick={handleBack}
          disabled={currentStep === 1 || isChecking}
        >
          <i className='fas fa-arrow-left me-2'></i>
          {intl.formatMessage({id: 'SYSTEM.BACK'})}
        </button>

        <div>
          <button className='btn btn-light me-3 px-6' onClick={onHide}>
            {intl.formatMessage({id: 'SYSTEM.MODAL.CLOSE'})}
          </button>

          {currentStep < 4 && (
            <button
              className='btn btn-primary px-6'
              disabled={!isNextEnabled() || isChecking}
              onClick={handleNext}
            >
              {intl.formatMessage({id: 'SYSTEM.NEXT'})}
              <i className='fas fa-arrow-right ms-2'></i>
            </button>
          )}

          {currentStep === 4 && !verificationResult && (
            <button
              className='btn btn-primary px-6'
              disabled={isChecking}
              onClick={handleVerifyStep}
            >
              {isChecking && <span className='spinner-border spinner-border-sm me-2'></span>}
              {intl.formatMessage({id: 'Scheduler.FOLDER.SHARE.VERIFY'})}
            </button>
          )}

          {currentStep === 4 && verificationResult && verificationResult.isSuccess && (
            <button
              className='btn btn-success px-6'
              disabled={!understood}
              onClick={() =>
                onCompleted({
                  ...verificationResult,
                  folderId: selectedFolder?.id,
                  folderName: selectedFolder?.name,
                })
              }
            >
              <i className='fas fa-check me-2'></i>
              {intl.formatMessage({id: 'SYSTEM.COMPLETE'})}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default FolderVerifyStepsModal
